<template>
    <div class="pun-upload-page">
        <div class="page-header">
            <div class="page-title-block">
                <h1 class="page-title">PUN Orario</h1>
                <p class="page-subtitle">
                    Carica i file PUN e consulta i valori orari caricati a sistema.
                </p>
            </div>
        </div>

        <div class="layout">
            <!-- LEFT: upload (con input nativo, ma stessa logica uploadPunFile(file)) -->
            <div class="card card-left">
                <div class="card-head">
                    <div>
                        <div class="card-title">Gestione dati</div>
                        <div class="card-desc">Importa i dataset PUN più recenti.</div>
                    </div>
                </div>

                <div class="dropzone" :class="{ disabled: uploading }">
                    <div class="drop-icon">
                        <i class="pi pi-cloud-upload"></i>
                    </div>

                    <div class="drop-title">Caricamento dati PUN</div>
                    <div class="drop-sub">Trascina e rilascia file CSV o Excel</div>

                    <!-- input hidden -->
                    <input ref="fileInput" type="file" class="hidden-input" accept=".csv,.xlsx" @change="onFileSelected"
                        :disabled="uploading" />

                    <!-- bottoni come prima (ora totalmente controllati) -->
                    <div class="upload-buttons">
                        <Button label="Seleziona file" icon="pi pi-plus" class="p-button-info" :disabled="uploading"
                            @click="openPicker" />
                        <Button label="Carica" icon="pi pi-upload" class="p-button-info"
                            :disabled="!selectedFile || uploading" :loading="uploading" @click="uploadSelectedFile" />
                        <Button label="Rimuovi" icon="pi pi-times" class="p-button-danger"
                            :disabled="!selectedFile || uploading" @click="clearSelectedFile" />
                    </div>

                    <!-- sotto: solo filename -->
                    <div class="filename-box">
                        <div v-if="selectedFile" class="filename-text" :title="selectedFile.name">
                            {{ selectedFile.name }}
                        </div>
                        <div v-else class="filename-empty">Nessun file selezionato</div>
                    </div>

                    <div v-if="uploading" class="drop-loading">
                        <i class="pi pi-spin pi-spinner"></i>
                        <span>Caricamento in corso...</span>
                    </div>

                    <p v-if="lastUploadAt" class="last-upload">
                        Ultimo caricamento: <strong>{{ lastUploadAt }}</strong>
                    </p>
                </div>

                <div class="left-actions">
                    <Button label="Cronologia file" icon="pi pi-history" class="p-button-info w-100"
                        @click="openHistoryDialog" :disabled="uploading" />
                </div>

                <div class="left-footer">
                    <i class="pi pi-info-circle"></i>
                    <span>Dimensione massima file: 10MB. I record vengono validati automaticamente.</span>
                </div>
            </div>

            <!-- RIGHT: tabella PUN come prima -->
            <div class="card card-right">
                <div class="right-head">
                    <div>
                        <div class="card-title">Dati PUN caricati</div>
                        <div class="card-desc">
                            Visualizza i valori PUN per data e ora.
                        </div>
                    </div>

                    <div class="right-actions">
                        <Button icon="pi pi-refresh"
                            class="action-square p-button-rounded p-button-outlined p-button-info"
                            :disabled="loadingTable || uploading" @click="loadPunRows" v-tooltip.bottom="'Aggiorna'" />
                    </div>
                </div>

                <div v-if="showFilters" class="filters-row">
                    <span class="p-float-label date-filter">
                        <Calendar v-model="dateFilter" :showIcon="true" dateFormat="yy-mm-dd" inputId="pun-date-filter"
                            class="w-100 pun-calendar" :disabled="loadingTable" />
                        <label for="pun-date-filter">Filtra per data</label>
                    </span>

                    <Button v-if="dateFilter" icon="pi pi-filter-slash" class="p-button-text p-button-sm p-button-info"
                        @click="clearFilters" :disabled="loadingTable" v-tooltip.bottom="'Pulisci filtro'" />
                </div>

                <DataTable :value="filteredRows" :loading="loadingTable" :paginator="true" :rows="24"
                    :rowsPerPageOptions="[24, 48, 96]" responsiveLayout="scroll" class="pun-table">
                    <Column field="date" header="Data" sortable style="width: 9rem" />
                    <Column field="hour" header="Ora" sortable style="width: 6rem" />
                    <Column field="value" header="Valore" sortable style="width: 8rem" bodyClass="text-right" />

                    <template #empty>
                        <div class="empty-state">
                            <i class="pi pi-info-circle empty-icon"></i>
                            <span>Nessun dato PUN disponibile.</span>
                        </div>
                    </template>
                </DataTable>
            </div>
        </div>

        <!-- Dialog cronologia -->
        <Dialog v-model:visible="historyDialogVisible" header="Cronologia file caricati" :modal="true"
            :draggable="false" class="history-dialog" :style="{ width: '44rem' }">
            <DataTable :value="historyFiles" :loading="historyLoading" responsiveLayout="scroll" class="history-table">
                <Column field="uploaded_at" header="Data caricamento" style="width: 12rem" />
                <Column field="file_name" header="Nome file" />
                <Column field="rows_count" header="Righe" style="width: 6rem" />

                <Column header="Azioni" style="width: 7rem">
                    <template #body="slotProps">
                        <Button icon="pi pi-download" class="p-button-text p-button-sm"
                            @click="downloadHistoryFile(slotProps.data)" :tooltip="'Scarica file'" />
                    </template>
                </Column>

                <template #empty>
                    <div class="empty-state">
                        <i class="pi pi-info-circle empty-icon"></i>
                        <span>Nessun file in cronologia.</span>
                    </div>
                </template>
            </DataTable>
        </Dialog>
    </div>
</template>

<script>
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import Calendar from "primevue/calendar";

import Toast from "primevue/toast";
import { useToast } from "primevue/usetoast";

import PunService from "@/services/pun/pun-service";

export default {
    name: "PunUploadPage",
    components: {
        DataTable,
        Column,
        Button,
        Dialog,
        Calendar,
    },
    data() {
        return {
            service: null,

            // upload
            selectedFile: null,
            uploading: false,
            lastUploadAt: null,

            // table
            punRows: [],
            dateFilter: null,
            loadingTable: false,
            showFilters: true,

            // history
            historyDialogVisible: false,
            historyLoading: false,
            historyFiles: [],
        };
    },
    computed: {
        filteredRows() {
            if (!this.dateFilter) {
                return this.punRows;
            }

            const yyyy = this.dateFilter.getFullYear();
            const mm = String(this.dateFilter.getMonth() + 1).padStart(2, "0");
            const dd = String(this.dateFilter.getDate()).padStart(2, "0");
            const filterDateStr = `${yyyy}-${mm}-${dd}`;

            return this.punRows.filter((row) => row.date === filterDateStr);
        },
    },
    created() {
        const bus = this.$eventBus || this.eventBus;

        this.toast = useToast();
        this.service = new PunService(bus);

        this._toastHandler = (t) => {
            this.toast.add({
                severity: t.severity,
                summary: t.summary,
                detail: t.detail,
                life: 3500,
            });
        };

        if (bus?.on) {
            bus.on("toast", this._toastHandler);
        } else if (bus?.$on) {
            bus.$on("toast", this._toastHandler);
        }
    },
    mounted() {
        this.loadPunRows();
    },
    methods: {
        toggleFilters() {
            this.showFilters = !this.showFilters;
        },

        async loadPunRows() {
            this.loadingTable = true;
            try {
                const res = await this.service.getPunRows();
                this.punRows = res && res.data ? res.data : [];
            } finally {
                this.loadingTable = false;
            }
        },

        // ---- upload input nativo ----
        openPicker() {
            this.$refs.fileInput?.click();
        },

        onFileSelected(e) {
            const file = e?.target?.files?.[0] || null;
            this.selectedFile = file;
        },

        clearSelectedFile() {
            this.selectedFile = null;
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = "";
            }
        },

        async uploadSelectedFile() {
            if (!this.selectedFile) {
                return;
            }

            this.uploading = true;

            try {
                // LOGICA IDENTICA: chiama sempre uploadPunFile(file)
                const res = await this.service.uploadPunFile(this.selectedFile);

                this.lastUploadAt = res && res.uploaded_at ? res.uploaded_at : this.lastUploadAt;

                // alla fine: rimuovi file dalla sezione
                this.clearSelectedFile();

                // ricarica tabella dalla fonte ufficiale
                await this.loadPunRows();

                if (this.historyDialogVisible) {
                    await this.loadHistoryFiles();
                }
            } catch (e) {
                // pulisci comunque selezione per evitare stati strani
                this.clearSelectedFile();
                throw e;
            } finally {
                this.uploading = false;
            }
        },

        // ---- filters ----
        clearFilters() {
            this.dateFilter = null;
        },

        // ---- history ----
        openHistoryDialog() {
            this.historyDialogVisible = true;
            this.loadHistoryFiles();
        },

        async loadHistoryFiles() {
            this.historyLoading = true;
            try {
                const res = await this.service.getHistory();
                this.historyFiles = res && res.data ? res.data : [];
            } finally {
                this.historyLoading = false;
            }
        },

        downloadHistoryFile(item) {
            this.service.downloadHistoryFile(item);
        },
    },
};
</script>

<style scoped>
.pun-upload-page {
    --primary: #1f5eff;
    --bg: #f4f6fb;
    --text: #0f172a;
    --muted: #64748b;
    --border: #e6ebf5;
    --card: #ffffff;

    min-height: 100vh;
    background: var(--bg);
    padding: 22px;
    box-sizing: border-box;
    max-width: 1200px;
    margin: 0 auto;
    border-radius: 15px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1) !important;
}

.page-header {
    padding: 6px 6px 14px;
}

.page-title-block {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.page-title {
    margin: 0;
    font-size: 34px;
    font-weight: 800;
    color: var(--text);
    letter-spacing: -0.02em;
}

.page-subtitle {
    margin: 0;
    color: var(--muted);
}

.layout {
    display: grid;
    grid-template-columns: 380px 1fr;
    gap: 18px;
    padding: 0 6px;
}

.card {
    background: var(--card);
    border: 1px solid var(--border);
    border-radius: 14px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5) !important;
    overflow: hidden;
}

.card-head,
.right-head {
    padding: 18px 18px 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.card-title {
    font-weight: 800;
    color: var(--text);
    letter-spacing: -0.01em;
}

.card-desc {
    margin-top: 4px;
    font-size: 13px;
    color: var(--muted);
}

/* LEFT */
.dropzone {
    margin: 0 18px;
    border: 2px dashed #d7def2;
    border-radius: 14px;
    background: #fafbff;
    padding: 18px 16px 14px;
    position: relative;
    text-align: center;
}

.dropzone.disabled {
    opacity: 0.7;
    pointer-events: none;
}

.drop-icon {
    width: 54px;
    height: 54px;
    border-radius: 50%;
    margin: 0 auto 10px;
    display: grid;
    place-items: center;
    background: rgba(31, 94, 255, 0.1);
    color: var(--primary);
    border: 1px solid rgba(31, 94, 255, 0.18);
    font-size: 20px;
}

.drop-title {
    font-weight: 800;
    color: var(--text);
}

.drop-sub {
    margin-top: 4px;
    font-size: 12.5px;
    color: var(--muted);
}

/* input */
.hidden-input {
    display: none;
}

/* buttons (spazio tra i tasti, come richiesto) */
.upload-buttons {
    margin-top: 12px;
    display: flex;
    justify-content: center;
    gap: 12px;
    flex-wrap: wrap;
}

.filename-box {
    margin-top: 12px;
    border: 1px solid #e6ebf5;
    border-radius: 12px;
    background: #ffffff;
    padding: 10px 12px;
    text-align: left;
}

.filename-text {
    font-weight: 700;
    color: #0f172a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.filename-empty {
    color: #64748b;
    font-size: 13px;
}

.drop-loading {
    margin-top: 10px;
    display: inline-flex;
    gap: 8px;
    align-items: center;
    color: var(--muted);
    font-size: 13px;
}

.last-upload {
    margin: 10px 0 0;
    font-size: 12.5px;
    color: var(--muted);
}

.left-actions {
    padding: 14px 18px 8px;
}

.left-footer {
    margin: 10px 18px 14px;
    padding: 10px 12px;
    border-radius: 12px;
    background: #f6f8ff;
    border: 1px solid #e7ecff;
    color: var(--muted);
    font-size: 12px;
    display: flex;
    align-items: flex-start;
    gap: 8px;
}

.w-100 {
    width: 100%;
}

.btn-secondary {
    height: 44px;
    border-radius: 12px;
    font-weight: 700;
}

/* RIGHT */
.card-right {
    padding-bottom: 10px;
}

.right-actions {
    display: flex;
    gap: 10px;
}

.action-square {
    width: 42px;
    height: 42px;
    border-radius: 12px;
}

.filters-row {
    padding: 0 18px 10px;
    display: flex;
    align-items: center;
    gap: 10px;
}

.date-filter {
    width: 220px;
}

.text-right {
    text-align: right;
}

/* Table */
.pun-table {
    padding: 0 6px;
}

/* Header visibili + colore richiesto */
.pun-table :deep(.p-datatable-thead > tr > th),
.history-table :deep(.p-datatable-thead > tr > th) {
    background: #068de0 !important;
    color: #ffffff !important;
    font-weight: 800 !important;
    opacity: 1 !important;
    visibility: visible !important;
    border-color: rgba(255, 255, 255, 0.25) !important;
}

.pun-table :deep(.p-datatable-thead),
.history-table :deep(.p-datatable-thead) {
    opacity: 1 !important;
    visibility: visible !important;
    display: table-header-group !important;
}

.pun-table :deep(.p-datatable-tbody > tr > td),
.history-table :deep(.p-datatable-tbody > tr > td) {
    border-color: #eef2ff;
    padding: 14px 14px;
    color: var(--text);
}

.empty-state {
    padding: 14px 10px;
    display: flex;
    gap: 10px;
    align-items: center;
    color: var(--muted);
}

.empty-icon {
    font-size: 1.1rem;
}

/* Dialog */
.history-dialog :deep(.p-dialog) {
    border-radius: 14px;
}

.pun-calendar :deep(.p-datepicker-trigger) {
    background: #068de0 !important;
    border-color: #068de0 !important;
    color: #ffffff !important;
}

.pun-calendar :deep(.p-datepicker-trigger:hover) {
    filter: brightness(0.95);
}

.pun-calendar :deep(.p-datepicker-trigger:focus) {
    box-shadow: 0 0 0 0.2rem rgba(6, 141, 224, 0.35) !important;
}

/* Responsive */
@media (max-width: 1100px) {
    .layout {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 620px) {
    .page-title {
        font-size: 26px;
    }
}
</style>